import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { sitesApi } from '@/api/sites';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/shared/Button';
import { safeDashboardAccent } from '@/lib/dashboardAccent';
import { parseTheme } from '@/lib/themeParse';
import { isDirty as readDirty, dirtyLabel } from '@/hooks/useDirtyBuffer';
import type { Site } from '@/types';

// LAST_SITE_SLUG_KEY is the localStorage key for the last-active site slug.
// Written on every successful site-scoped page mount; read via resolveLastSite
// below (by RootRoute when resolving `/`, and by the workspace nav's sticky
// empty state).
export const LAST_SITE_SLUG_KEY = 'aeo.lastSiteSlug';

// resolveLastSite picks the "sticky" site that the dashboard points at when
// there is no site in the URL. It is the SINGLE source of truth shared by
// RootRoute (resolving `/` to a landing page) and the workspace nav column
// (keeping its site-scoped items live while the operator is on a global
// route). The two MUST agree — otherwise `/` redirects to a different site
// than the nav highlights. Resolution order, given a non-empty list:
//   1. the stored last-active slug, if it still resolves to a site;
//   2. else the first active site;
//   3. else the first site of any status.
// Returns null only when no site exists at all (fresh account) — callers use
// that to render the dashed "Select a site" empty state.
// eslint-disable-next-line react-refresh/only-export-components
export function resolveLastSite(allSites: Site[]): Site | null {
  if (allSites.length === 0) return null;
  try {
    const stored = window.localStorage.getItem(LAST_SITE_SLUG_KEY);
    if (stored) {
      const match = allSites.find((s) => s.slug === stored);
      if (match) return match;
    }
  } catch {
    // ignored — private mode / disabled storage falls through to defaults
  }
  return allSites.find((s) => s.status === 'active') ?? allSites[0] ?? null;
}

interface SiteContextValue {
  // currentSite: null on global routes (and during the initial fetch), the
  // resolved site on site-scoped routes.
  currentSite: Site | null;
  // allSites: every site returned by the backend (including decommissioned).
  // Pages filter as needed.
  allSites: Site[];
  // setSite navigates to /sites/<slug>/<currentSubPath>, preserving the
  // sub-path. On a global route, navigates to /sites/<slug>.
  setSite: (slug: string) => void;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SiteContext = createContext<SiteContextValue | null>(null);

// UnsavedSwitchDetail is the payload of the `aeo:unsaved-site-switch` event.
// SiteContext dispatches it when a switch is requested while a dirty buffer
// is active; <UnsavedSwitchModal /> consumes it and confirms with the user.
export interface UnsavedSwitchDetail {
  targetSlug: string;
  label: string;
  proceed: () => void;
}

// SiteProvider centralises the active-site selection. The URL is the
// canonical source of truth (the `:slug` segment of `/sites/:slug/...`);
// `allSites` is a cached list used to resolve the slug to a full Site. There
// is no internal "active site ID" state — currentSite is derived from the
// URL on every render.
export function SiteProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await sitesApi.list();
      setAllSites(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSites();
  }, [fetchSites]);

  // One-time cleanup: pre-N1 builds wrote `aeo.lastSite` (a site ID); N1
  // switched to `aeo.lastSiteSlug`. The old key is unused but harmless to
  // leave around — clear it on every mount to keep localStorage tidy.
  useEffect(() => {
    try {
      window.localStorage.removeItem('aeo.lastSite');
    } catch {
      // ignored
    }
  }, []);

  // currentSlugFromURL: matches /sites/<slug> or /sites/<slug>/<anything>.
  // The match is done off location.pathname rather than useParams so the
  // provider works at the top of the tree (above the <Route> elements that
  // bind :slug to params). The exact `/sites` lobby path is excluded.
  const currentSlugFromURL = useMemo<string | null>(() => {
    const m = /^\/sites\/([^/]+)(?:\/|$)/.exec(location.pathname);
    if (!m) return null;
    const slug = m[1];
    if (!slug) return null;
    return slug;
  }, [location.pathname]);

  const currentSite = useMemo<Site | null>(() => {
    if (!currentSlugFromURL) return null;
    return allSites.find((s) => s.slug === currentSlugFromURL) ?? null;
  }, [allSites, currentSlugFromURL]);

  // Persist last-active slug whenever a site-scoped page resolves to a known
  // site. Used only by RootRoute when resolving `/` to the last-visited site.
  useEffect(() => {
    if (!currentSite) return;
    try {
      window.localStorage.setItem(LAST_SITE_SLUG_KEY, currentSite.slug);
    } catch {
      // ignored
    }
  }, [currentSite]);

  const setSite = useCallback(
    (nextSlug: string) => {
      const proceed = () => {
        const path = location.pathname;
        const m = /^\/sites\/[^/]+(\/.*)?$/.exec(path);
        const subPath = m && m[1] ? m[1] : '';
        navigate(`/sites/${nextSlug}${subPath}${location.search}`);
      };
      if (readDirty()) {
        // Dispatch to the top-level <UnsavedSwitchModal /> in ProtectedLayout
        // so SiteContext doesn't own React UI. The modal calls `proceed()`
        // on confirm.
        window.dispatchEvent(
          new CustomEvent<UnsavedSwitchDetail>('aeo:unsaved-site-switch', {
            detail: { targetSlug: nextSlug, label: dirtyLabel(), proceed },
          }),
        );
        return;
      }
      proceed();
    },
    [navigate, location.pathname, location.search],
  );

  const value: SiteContextValue = useMemo(
    () => ({
      currentSite,
      allSites,
      setSite,
      loading,
      error,
      refresh: fetchSites,
    }),
    [currentSite, allSites, setSite, loading, error, fetchSites],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

// useCurrentSite is the canonical hook. Throws if called outside a
// SiteProvider. `currentSite` is null on global routes; consumers that
// require a site should be rendered inside <SiteLayout>.
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useCurrentSite must be used within <SiteProvider>');
  return ctx;
}

// useCurrentSiteOptional returns null when called outside a provider (test
// harness, isolated story). Kept for components that read the site only as
// a fallback for rendering.
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentSiteOptional(): SiteContextValue | null {
  return useContext(SiteContext);
}

// SiteLayout is the route element for /sites/:slug. It verifies the slug
// resolves to a real site, handles UUID-shaped legacy bookmarks, shows a
// 404 on a missing site, and a warning banner on decommissioned sites. It
// renders <Outlet /> for the child route.
export function SiteLayout() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { allSites, loading, currentSite } = useCurrentSite();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  // Legacy bookmark: a UUID-shaped path was used. Look up the site by id
  // and replace with the slug-prefixed URL.
  useEffect(() => {
    if (redirectedRef.current) return;
    if (loading) return;
    if (currentSite) return;
    if (!slug) return;
    const looksLikeID =
      /^site_/.test(slug) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
    if (!looksLikeID) return;
    const byID = allSites.find((s) => s.id === slug);
    if (byID) {
      redirectedRef.current = true;
      navigate(`/sites/${byID.slug}`, { replace: true });
    }
  }, [loading, currentSite, allSites, slug, navigate]);

  // Derive a legible dashboard accent from the active site's theme. Clamped
  // via safeDashboardAccent so a too-pale or too-dark site palette still
  // produces a readable chrome accent on the dark dashboard background.
  const accent = useMemo<string | undefined>(() => {
    if (!currentSite) return undefined;
    try {
      const theme = parseTheme(currentSite.theme);
      return safeDashboardAccent(theme.palette.accent);
    } catch {
      return undefined;
    }
  }, [currentSite]);

  // The header accent strip lives in <Header /> and the nav's active-item
  // border-left lives in <SiteWorkspaceNav /> / <GlobalRail /> — all rendered
  // outside <SiteLayout /> in the shell. To make `--site-accent` available to
  // those chrome components, set it on <html> and clear it when leaving a
  // site-scoped route. This survives every nested element.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (accent) {
      root.style.setProperty('--site-accent', accent);
    } else {
      root.style.removeProperty('--site-accent');
    }
    return () => {
      root.style.removeProperty('--site-accent');
    };
  }, [accent]);

  const accentStyle = useMemo<CSSProperties | undefined>(() => {
    if (!accent) return undefined;
    return { ['--site-accent' as string]: accent } as CSSProperties;
  }, [accent]);

  if (loading && allSites.length === 0) {
    return <LoadingSpinner label="Loading site" />;
  }

  if (!currentSite) {
    return (
      <EmptyState
        title="Site not found"
        description={`No site with slug "${slug}".`}
        action={
          <Button variant="primary" size="sm" onClick={() => navigate('/sites')}>
            Back to sites
          </Button>
        }
      />
    );
  }

  if (currentSite.status === 'decommissioned') {
    return (
      <div className="contents" style={accentStyle}>
        <div className="space-y-4">
          <div className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
            This site is decommissioned. Pipeline, publishing, and analytics are
            disabled.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => navigate('/sites')}
            >
              Back to sites
            </button>
            .
          </div>
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div className="contents" style={accentStyle}>
      <Outlet />
    </div>
  );
}
